import { makeStyles } from '@material-ui/styles'
import React, { useCallback } from 'react'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Info from 'core/components/validatedForm/Info'
import Button from 'core/elements/button'
import Modal from 'core/elements/modal'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { getCurrentUser } from 'openstack/helpers'
import { powerVirtualMachine } from './actions'
import { hasOtherUserVm } from './helpers'

const getActionBody = (action, type = 'SOFT') => {
  return {
    suspend: { suspend: null },
    pause: { pause: null },
    unpause: { unpause: null },
    start: { 'os-start': null },
    stop: { 'os-stop': null },
    resume: { resume: null },
    reboot: { reboot: { type } },
  }[action]
}

export default function PowerVirtualMachineDialog({ rows: vms, action, type = 'SOFT', onClose }) {
  const classes = useStyles()
  const {
    update: powerFn,
    updating: poweringVm,
    error: powerError,
  } = useUpdateAction(powerVirtualMachine)
  const title = `${action[0].toUpperCase() + action.slice(1)} VMs`
  const powerErrorMessage =
    powerError?.message || 'Unable to perform VM power action. Please try again.'
  const handleAction = useCallback(async () => {
    const body = getActionBody(action, type)
    let allSucceeded = true
    for (const vm of vms || []) {
      const { success } = await powerFn({ id: vm.id, body })
      if (!success) {
        allSucceeded = false
        break
      }
    }

    if (allSucceeded) onClose(true)
  }, [action, onClose, powerFn, type, vms])

  const { role: userRole, userId } = getCurrentUser()

  return (
    <Modal
      panel="dialog"
      open
      onClose={onClose}
      title={title}
      maxWidth={600}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={poweringVm}>
            Cancel
          </Button>
          <Button onClick={handleAction} loading={poweringVm}>
            Confirm
          </Button>
        </>
      }
    >
      {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
        <Info error>NOTE: You are performing an operation on another user's VM.</Info>
      )}
      <Text variant="body2">You are about to {action} the following VMs:</Text>
      <div className={classes.vmsToPower}>
        {vms?.map((vm) => (
          <Text key={vm.id} variant="body2">
            <strong>{vm.name || vm.id}</strong>
          </Text>
        ))}
      </div>
      {!!powerError && <ErrorMessage>{powerErrorMessage}</ErrorMessage>}
    </Modal>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  vmsToPower: {
    display: 'grid',
    gap: 4,
  },
}))
